/**
 * Enhanced Sync Service - Robust Offline Synchronization
 * Version: 2.0.0
 * Date: February 11, 2026
 * 
 * Improvements:
 * - Exponential backoff for retries
 * - Priority-based sync ordering
 * - Conflict detection and resolution
 * - Better error handling
 * - Batch sync operations
 * - Real-time sync status updates
 */

import api from '../config/axiosConfig';
import { enhancedIndexedDBService, PendingSale, SyncQueueItem } from './enhancedIndexedDBService';

// Interfaces
export interface SyncProgress {
  current: number;
  total: number;
  eta?: number;
}

export type SyncStatusType = 'idle' | 'syncing' | 'completed' | 'failed' | 'paused';

interface SyncStatus {
  status: SyncStatusType;
  progress: number;
  current?: string;
  total?: number;
  synced?: number;
  failed?: number;
  error?: string;
  estimatedTimeRemaining?: number;
}

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{id: string; error: string}>;
  duration: number;
}

interface ConflictResolution {
  strategy: 'server-wins' | 'client-wins' | 'merge' | 'manual';
  data?: any;
}

class EnhancedSyncService {
  private isSyncing = false;
  private isPaused = false;
  private syncListeners: Array<(status: SyncStatus) => void> = [];
  private backgroundSyncSupported = false;
  private syncInterval: number | null = null;
  private retryDelays = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff
  
  constructor() {
    this.checkBackgroundSyncSupport();
    this.initNetworkListeners();
    this.startPeriodicSync();
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================

  private checkBackgroundSyncSupport() {
    this.backgroundSyncSupported = 
      'serviceWorker' in navigator && 
      'sync' in (ServiceWorkerRegistration.prototype as any);
    
    if (this.backgroundSyncSupported) {
      console.log('✓ Background Sync API supported');
      this.registerBackgroundSync();
    } else {
      console.log('⚠ Background Sync API not supported, using fallback');
    }
  }

  private initNetworkListeners() {
    // Listen for network status changes
    window.addEventListener('online', () => {
      console.log('🌐 Network back online - triggering sync');
      this.syncAll();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Network offline - pausing sync');
      this.isPaused = true;
      this.notifyListeners({
        status: 'paused',
        progress: 0
      });
    });

    // Auto-sync on page load if online
    if (navigator.onLine) {
      setTimeout(() => {
        this.syncAll();
      }, 2000); // Wait 2 seconds after load
    }
  }

  private async registerBackgroundSync() {
    if (!this.backgroundSyncSupported) return;

    try {
      const registration = await navigator.serviceWorker.ready as any;
      await registration.sync.register('sync-pending-data');
      console.log('✓ Background sync registered');
      
      // Listen for sync completion messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          this.handleBackgroundSyncComplete(event.data);
        }
      });
    } catch (error) {
      console.error('Failed to register background sync:', error);
    }
  }

  private handleBackgroundSyncComplete(data: any) {
    console.log('Background sync completed:', data);
    this.notifyListeners({
      status: 'completed',
      progress: 100,
      synced: data.synced,
      failed: data.failed
    });

    // Refresh stats
    this.refreshStats();
  }

  private startPeriodicSync() {
    // Sync every 2 minutes when online
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && !this.isSyncing && !this.isPaused) {
        this.syncAll();
      }
    }, 2 * 60 * 1000);
  }

  // ============================================================
  // SYNC LISTENERS
  // ============================================================

  onSyncStatusChange(callback: (status: SyncStatus) => void): () => void {
    this.syncListeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback);
    };
  }

  private notifyListeners(status: SyncStatus) {
    this.syncListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in sync listener:', error);
      }
    });

    // Also dispatch custom events for components that use addEventListener
    window.dispatchEvent(new CustomEvent('sync-status-changed', { 
      detail: status.status 
    }));

    // Dispatch progress if available
    if (status.total && status.synced !== undefined) {
      window.dispatchEvent(new CustomEvent('sync-progress', { 
        detail: {
          current: status.synced,
          total: status.total,
          eta: status.estimatedTimeRemaining
        }
      }));
    }
  }

  // ============================================================
  // MAIN SYNC LOGIC
  // ============================================================

  /**
   * Sync all pending data with priority ordering
   */
  async syncAll(): Promise<SyncResult> {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping...');
      return { success: true, synced: 0, failed: 0, errors: [], duration: 0 };
    }

    if (!navigator.onLine) {
      console.log('Cannot sync while offline');
      return { 
        success: false, 
        synced: 0, 
        failed: 0, 
        errors: [{ id: 'network', error: 'Network is offline' }], 
        duration: 0 
      };
    }

    this.isSyncing = true;
    this.isPaused = false;
    const startTime = Date.now();

    this.notifyListeners({ 
      status: 'syncing', 
      progress: 0,
      synced: 0,
      failed: 0
    });

    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
      duration: 0
    };

    try {
      console.log('🔄 Starting sync...');

      // Get total items to sync
      const [pendingSales, syncQueue] = await Promise.all([
        enhancedIndexedDBService.getPendingSalesByPriority(),
        enhancedIndexedDBService.getSyncQueue()
      ]);

      const totalItems = pendingSales.length + syncQueue.length;

      if (totalItems === 0) {
        console.log('✓ No pending data to sync');
        this.notifyListeners({
          status: 'completed',
          progress: 100,
          synced: 0,
          failed: 0
        });
        result.duration = Date.now() - startTime;
        return result;
      }

      console.log(`📊 Total items to sync: ${totalItems} (${pendingSales.length} sales, ${syncQueue.length} queue)`);

      // Sync pending sales (high priority)
      const salesResult = await this.syncPendingSales(pendingSales, totalItems);
      result.synced += salesResult.synced;
      result.failed += salesResult.failed;
      result.errors.push(...salesResult.errors);

      // Sync general queue
      const queueResult = await this.syncQueue(syncQueue, totalItems, result.synced);
      result.synced += queueResult.synced;
      result.failed += queueResult.failed;
      result.errors.push(...queueResult.errors);

      result.success = result.failed === 0;
      result.duration = Date.now() - startTime;

      // Update metadata
      await enhancedIndexedDBService.updateSyncMetadata('last_sync', Date.now());

      // Notify completion
      this.notifyListeners({
        status: result.success ? 'completed' : 'failed',
        progress: 100,
        synced: result.synced,
        failed: result.failed
      });

      console.log(`✓ Sync completed: ${result.synced} synced, ${result.failed} failed (${result.duration}ms)`);

    } catch (error) {
      console.error('💥 Sync error:', error);
      result.success = false;
      result.errors.push({ 
        id: 'system', 
        error: error instanceof Error ? error.message : 'Unknown error' 
      });
      result.duration = Date.now() - startTime;
      
      this.notifyListeners({
        status: 'failed',
        progress: 0,
        error: error instanceof Error ? error.message : 'Sync failed'
      });
    } finally {
      this.isSyncing = false;
    }

    return result;
  }

  /**
   * Sync pending sales with retry logic
   */
  private async syncPendingSales(
    sales: PendingSale[], 
    totalItems: number
  ): Promise<SyncResult> {
    const result: SyncResult = { 
      success: true, 
      synced: 0, 
      failed: 0, 
      errors: [], 
      duration: 0 
    };

    if (sales.length === 0) return result;

    console.log(`💰 Syncing ${sales.length} pending sales...`);

    for (let i = 0; i < sales.length; i++) {
      const sale = sales[i];
      
      // Check if paused
      if (this.isPaused || !navigator.onLine) {
        console.log('⏸️  Sync paused');
        break;
      }

      try {
        // Update status
        this.notifyListeners({
          status: 'syncing',
          progress: Math.round(((result.synced + result.failed) / totalItems) * 100),
          current: `Syncing sale ${sale.local_id}`,
          total: totalItems,
          synced: result.synced,
          failed: result.failed
        });

        // Mark as syncing
        await enhancedIndexedDBService.updatePendingSaleStatus(sale.id, 'syncing');

        // Attempt sync
        const response = await this.syncSaleWithRetry(sale);

        if (response.success) {
          // Success - remove from queue
          await enhancedIndexedDBService.deletePendingSale(sale.id);
          result.synced++;
          console.log(`✓ Synced sale ${sale.local_id}`);
        } else {
          throw new Error(response.error || 'Sync failed');
        }

      } catch (error) {
        console.error(`✗ Failed to sync sale ${sale.local_id}:`, error);
        
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        
        // Update status to failed
        await enhancedIndexedDBService.updatePendingSaleStatus(sale.id, 'failed', errorMessage);
        
        result.failed++;
        result.errors.push({ id: sale.id, error: errorMessage });
        
        // Continue with next sale (don't stop on failure)
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Sync single sale with exponential backoff retry
   */
  private async syncSaleWithRetry(
    sale: PendingSale,
    retryCount: number = 0
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await api.post('/sales/', sale.sale_data);

      if (response.status === 200 || response.status === 201) {
        return { success: true };
      } else {
        throw new Error(`Server returned status ${response.status}`);
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      // Check if should retry
      if (retryCount < sale.max_retries && this.isRetryableError(error)) {
        const delay = this.getRetryDelay(retryCount);
        console.log(`⏳ Retrying sale ${sale.local_id} in ${delay}ms (attempt ${retryCount + 1}/${sale.max_retries})`);
        
        await this.sleep(delay);
        return this.syncSaleWithRetry(sale, retryCount + 1);
      }

      return { success: false, error: errorMessage };
    }
  }

  /**
   * Sync general queue items
   */
  private async syncQueue(
    queueItems: SyncQueueItem[],
    totalItems: number,
    alreadySynced: number
  ): Promise<SyncResult> {
    const result: SyncResult = { 
      success: true, 
      synced: 0, 
      failed: 0, 
      errors: [], 
      duration: 0 
    };

    if (queueItems.length === 0) return result;

    console.log(`📋 Syncing ${queueItems.length} queue items...`);

    for (const item of queueItems) {
      if (this.isPaused || !navigator.onLine) break;

      try {
        this.notifyListeners({
          status: 'syncing',
          progress: Math.round(((alreadySynced + result.synced + result.failed) / totalItems) * 100),
          current: `Syncing ${item.type}`,
          total: totalItems,
          synced: alreadySynced + result.synced,
          failed: result.failed
        });

        // Make API call
        const response = await api({
          method: item.method,
          url: item.endpoint,
          data: item.data
        });

        if (response.status >= 200 && response.status < 300) {
          await enhancedIndexedDBService.removeFromSyncQueue(item.id);
          result.synced++;
          console.log(`✓ Synced queue item ${item.id} (${item.type})`);
        } else {
          throw new Error(`Server returned status ${response.status}`);
        }

      } catch (error) {
        console.error(`✗ Failed to sync queue item ${item.id}:`, error);
        result.failed++;
        result.errors.push({ 
          id: item.id, 
          error: error instanceof Error ? error.message : 'Unknown error' 
        });
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  // ============================================================
  // HELPER METHODS
  // ============================================================

  private isRetryableError(error: any): boolean {
    // Retry on network errors or 5xx server errors
    if (error.message?.includes('Network')) return true;
    if (error.response?.status >= 500) return true;
    return false;
  }

  private getRetryDelay(retryCount: number): number {
    // Exponential backoff with jitter
    const baseDelay = this.retryDelays[Math.min(retryCount, this.retryDelays.length - 1)];
    const jitter = Math.random() * 1000; // Add 0-1s random jitter
    return baseDelay + jitter;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // ============================================================
  // PUBLIC API
  // ============================================================

  /**
   * Trigger background sync manually
   */
  async triggerBackgroundSync(): Promise<SyncResult | void> {
    if (!this.backgroundSyncSupported) {
      console.log('Background sync not supported, using regular sync');
      return await this.syncAll();
    }

    try {
      const registration = await navigator.serviceWorker.ready as any;
      await registration.sync.register('sync-pending-data');
      console.log('✓ Background sync triggered');
    } catch (error) {
      console.error('Failed to trigger background sync:', error);
      // Fallback to regular sync
      return await this.syncAll();
    }
  }

  /**
   * Force sync now (manual trigger)
   */
  async forceSyncNow(): Promise<SyncResult> {
    console.log('🔄 Force sync triggered by user');
    return await this.syncAll();
  }

  /**
   * Pause ongoing sync
   */
  pauseSync() {
    this.isPaused = true;
    console.log('⏸️  Sync paused');
    this.notifyListeners({
      status: 'paused',
      progress: 0
    });
  }

  /**
   * Resume paused sync
   */
  resumeSync() {
    this.isPaused = false;
    console.log('▶️  Sync resumed');
    if (navigator.onLine) {
      this.syncAll();
    }
  }

  /**
   * Get current sync stats
   */
  async getStats() {
    return await enhancedIndexedDBService.getStats();
  }

  /**
   * Get pending stats (for UI display)
   */
  async getPendingStats() {
    const stats = await this.getStats();
    return {
      total: (stats.pendingSales || 0) + (stats.pendingMutations || 0) + (stats.pendingReturns || 0),
      sales: stats.pendingSales || 0,
      mutations: stats.pendingMutations || 0,
      returns: stats.pendingReturns || 0
    };
  }

  /**
   * Manual sync trigger (for UI button)
   */
  async syncNow(): Promise<SyncResult> {
    console.log('🔄 Manual sync triggered');
    return await this.syncAll();
  }

  /**
   * Initialize service
   */
  async init() {
    console.log('✓ Enhanced Sync Service initialized');
    // Service is already initialized in constructor
    return Promise.resolve();
  }

  /**
   * Start auto-sync with specified interval
   */
  startAutoSync(intervalMs: number = 120000) {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
    
    this.syncInterval = setInterval(() => {
      if (navigator.onLine && !this.isSyncing && !this.isPaused) {
        console.log('⏰ Auto-sync triggered');
        this.syncAll();
      }
    }, intervalMs);
    
    console.log(`✓ Auto-sync started (${intervalMs / 1000}s intervals)`);
  }

  /**
   * Stop auto-sync
   */
  stopAutoSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
      console.log('⏹️  Auto-sync stopped');
    }
  }

  /**
   * Refresh stats and notify listeners
   */
  private async refreshStats() {
    const stats = await this.getStats();
    console.log('📊 Sync stats:', stats);
  }

  /**
   * Check if currently syncing
   */
  isSyncInProgress(): boolean {
    return this.isSyncing;
  }

  /**
   * Cleanup (call on app unmount)
   */
  destroy() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
    this.syncListeners = [];
    console.log('🧹 Sync service destroyed');
  }
}

// Export singleton instance
export const enhancedSyncService = new EnhancedSyncService();
export default enhancedSyncService;

// Export types (already exported inline above, remove duplicate export)
export type { SyncStatus, SyncResult, ConflictResolution };
